import {
  SkyDialog,
  SkyDropdown,
  SkyInput,
  SkyLink,
  SkyMessageBar,
  SkyRadioGroup,
  SkyText,
} from "@skypointcloud/skypoint-ui";
import { useCallback, useMemo, useRef, useState } from "react";
import { ROLE_LABELS, ROLE_RANK, grantableRoles, type RoleName } from "@shared/permissions";
import { isPersonalEmail } from "@shared/personalEmail";
import { GRANT_REASON_MIN_LENGTH, grantNeedsReason } from "@shared/types/access";
import type { AuthClientSetting } from "@shared/types/authClientConfig";
import {
  ADMIN_NEEDS_SSO_REASON,
  adminNeedsSsoMessage,
  adminNeedsSsoResetMessage,
  isMfaAdminRole,
} from "@shared/types/mfa";
import {
  SIGN_IN_METHOD_LABELS,
  offeredSignInMethods,
  signInMethodChoice,
  type SignInMethod,
} from "@shared/types/signInMethod";
import { scopeForRole, type AccessScope, type GrantInput } from "@/hooks/use-access";
import { useResetOnOpen } from "@/hooks/use-reset-on-open";
import { InlineErrorBar, type InlineFailure } from "@/lib/feedback/InlineErrorBar";

/**
 * Granting access by email (canvas 07, "Grant dialog" and "Break-glass reason").
 *
 * Three rules live here.
 *
 * **The role list is bounded by the grantor's own rank** (`grantableRoles`), so a
 * TenantAdmin never sees PlatformAdmin. The server enforces the same bound — this
 * is the courtesy half, and FR-13 says the two must agree rather than that either
 * one is the control.
 *
 * **Scope follows the role**, because `ROLE_SCOPES` assigns each role at exactly
 * one scope. A separate scope picker would offer combinations the server refuses,
 * so the scope is shown as a consequence and explained, not chosen.
 *
 * **SEC-18: a grant states why when the ROLE asks for it, not the tab**: an admin role or a
 * platform-scope role on every tab, and every grant through the platform surface
 * (`grantNeedsReason`, the rule the server applies). When the grantor is the grantee the dialog
 * says in words that this is recorded as break-glass. The warning is not a tooltip: it is the
 * thing the reviewer will read back, so the person writing it should have read it first.
 *
 * **Sign in with** (#1639 C0; shown as one line since #2023, owner 2026-10-26): the dialog picks
 * the method itself (`signInMethodChoice` over `defaultSignInMethod`: a provider's
 * `pinEmailDomains`, a personal mail domain to Email, else the default provider) and shows one
 * line, "Signs in with Microsoft" or "Signs in with email", with Change to open the two options.
 * With one method offered, or a role that allows only Microsoft (Platform admin under the
 * workforce-only rule, an admin role under the MFA floor), it shows nothing and sends the rule's
 * method. The server pins the method the request names, and refuses one that differs from how an
 * existing person already signs in. Moving a person to another provider is "Reset sign-in",
 * never the grant.
 *
 * **The invitation email** (product gaps row 19): a grant sends one when email is configured for
 * the deployment, and the dialog says which, from the status the page passes in. Unknown (still
 * loading, or the read failed) says nothing rather than guess.
 *
 * **One role per person per scope** (#1275, owner 2026-10-16): when the server answers that the
 * person already holds another role here (409 `ROLE_CHANGE_REQUIRED`), the page passes
 * `roleChange` and the dialog asks "Change Contributor to Instance admin on Development?". Only
 * Change role sends the grant again, with `replaceExisting`; Back returns to the form as typed.
 */

/** #1275: the confirmation a refused grant turns into, from the server's `currentRole` details. */
export interface GrantRoleChange {
  input: GrantInput;
  currentRoles: RoleName[];
}

/** "Contributor" or "Contributor and Viewer". */
function rolesSentence(roles: readonly RoleName[]): string {
  const labels = roles.map((role) => ROLE_LABELS[role]);
  if (labels.length < 2) return labels[0] ?? "their current role";
  return `${labels.slice(0, -1).join(", ")} and ${labels[labels.length - 1]}`;
}

/** The confirmation's question: "Change Contributor to Instance admin on Development?". */
export function roleChangeQuestion(change: GrantRoleChange, scopeName: string): string {
  return `Change ${rolesSentence(change.currentRoles)} to ${ROLE_LABELS[change.input.roleName]} on ${scopeName}?`;
}

/** What each role may do, so the dropdown explains the role rather than naming it. */
const ROLE_DESCRIPTIONS: Record<RoleName, string> = {
  PlatformAdmin: "Everything, in every tenant and instance",
  TenantAdmin: "Manages instances and people across one tenant",
  InstanceAdmin: "Runs and configures one instance, and grants access to it",
  Contributor: "Edits and runs connections and schedules in one instance",
  Viewer: "Reads connections, schedules, runs and history",
};

/** The invitation line (product gaps row 19), one per email status. */
const INVITE_SENT_NOTE = "An invitation email is sent to this address.";
const INVITE_NOT_CONFIGURED_NOTE = "Email is not configured. Tell the person how to sign in.";

/** The canvas asks for a reason a reviewer can act on, not the word "incident". */
const MIN_REASON_LENGTH = GRANT_REASON_MIN_LENGTH;

/** #1639 C0: what each "Sign in with" choice means, under its label. */
export const SIGN_IN_METHOD_HELP: Record<SignInMethod, string> = {
  microsoft:
    "They sign in with their organisation's Microsoft account. The invitation is sent now.",
  email:
    "For a person without a Microsoft work account. Skypoint creates their sign-in account, then you send the invitation with Resend invitation. The first time, they choose Forgot password to set a password.",
};

/** #1639 C0: the invitation line for an Email grant, which waits for its account. */
export const INVITE_EMAIL_PENDING_NOTE =
  "For a person new to Email sign-in, the invitation waits until Skypoint creates their account. Then use Resend invitation.";

/** #2023: the one line that names the method the grant pins, beside Change. */
export const SIGN_IN_METHOD_LINES = {
  microsoft: "Signs in with Microsoft.",
  email: "Signs in with email.",
} as const satisfies Record<SignInMethod, string>;

/** #1675: shown instead of the known-method row when a listed person is not on Microsoft. */
export const PLATFORM_ADMIN_KNOWN_METHOD_NOTE =
  "Platform admins sign in only with a Microsoft work account. Reset this person's sign-in to Microsoft first.";

/**
 * #1274: shown, never enforced. The grant stays allowed. The domain list is fixed and not tied to
 * the deployment's sign-in providers (accepted limitation), so the copy does not claim Microsoft
 * is the only way in.
 */
const PERSONAL_EMAIL_WARNING =
  "This looks like a personal address. Unless a sign-in option here accepts it, this person may not be able to sign in. Check the address and how they sign in before you continue.";

/** #2023: the same warning where the role allows a Microsoft work account only and shows no options. */
export const PLATFORM_ADMIN_PERSONAL_EMAIL_WARNING =
  "This looks like a personal address. Platform admins sign in only with a Microsoft work account, so this person may not be able to sign in.";
export const ADMIN_SSO_PERSONAL_EMAIL_WARNING = `This looks like a personal address. ${ADMIN_NEEDS_SSO_REASON} This person may not be able to sign in.`;

const EMAIL_SHAPE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

type ScopeType = ReturnType<typeof scopeForRole>;

const HINT_SCOPES: readonly ScopeType[] = ["instance", "tenant", "platform"];

function listOf(labels: readonly string[]): string {
  if (labels.length <= 1) return labels.join("");
  return `${labels.slice(0, -1).join(", ")} and ${labels[labels.length - 1]}`;
}

/**
 * The Scope field's hint: where each role this dialog offers is granted, and only those (#1374
 * item 2). An Instance admin is offered the instance roles only, so a hint naming Tenant admin and
 * Platform admin described roles the list beside it does not hold.
 */
export function scopeHint(roles: readonly RoleName[]): string {
  const offered = [...roles].sort((a, b) => ROLE_RANK[a] - ROLE_RANK[b]);
  const groups = HINT_SCOPES.map((scope) => ({
    scope,
    labels: offered.filter((role) => scopeForRole(role) === scope).map((r) => ROLE_LABELS[r]),
  })).filter((group) => group.labels.length > 0);
  return groups
    .map(({ scope, labels }, index) => {
      if (index > 0) return `${listOf(labels)} at ${scope} scope.`;
      return `${listOf(labels)} ${labels.length > 1 ? "are" : "is"} granted at ${scope} scope.`;
    })
    .join(" ");
}

function scopeNameOf(scopeType: ScopeType, tenantName: string, instanceName: string): string {
  if (scopeType === "platform") return "Platform";
  return scopeType === "tenant" ? tenantName : instanceName;
}

function reasonErrorOf(required: boolean, reason: string): string | undefined {
  if (!required || reason.trim().length >= MIN_REASON_LENGTH) return undefined;
  return `Say why, in at least ${MIN_REASON_LENGTH} characters. It is recorded on the audit event.`;
}

function emailErrorOf(email: string): string | undefined {
  if (!email.trim()) return "Enter the email address to grant access to.";
  return EMAIL_SHAPE.test(email.trim()) ? undefined : "Enter a complete email address.";
}

/** The dialog's title, description and primary label; a self-grant at platform scope says so. */
function dialogCopy(
  isBreakGlass: boolean,
  roleName: RoleName,
  scopeName: string,
): { title: string; description: string; action: string } {
  if (isBreakGlass) {
    return {
      title: `Grant ${ROLE_LABELS[roleName]} at platform scope`,
      description: "You are granting this role to yourself. Say why. The reason is recorded.",
      action: "Grant and record reason",
    };
  }
  return {
    title: `Grant access to ${scopeName}`,
    description: "A person who has not signed in yet appears as Pending until their first sign-in.",
    action: "Grant access",
  };
}

/** The invitation line (product gaps row 19), followed by the space before the next sentence. */
function InviteNote({
  emailConfigured,
  emailMethod,
}: {
  emailConfigured: boolean;
  emailMethod: boolean;
}) {
  let note = INVITE_SENT_NOTE;
  if (!emailConfigured) note = INVITE_NOT_CONFIGURED_NOTE;
  else if (emailMethod) note = INVITE_EMAIL_PENDING_NOTE;
  return (
    <>
      <span data-testid="grant-invite-note">{note}</span>{" "}
    </>
  );
}

export function GrantAccessDialog({
  open,
  onOpenChange,
  scope,
  grantorRole,
  selfEmail,
  instanceName,
  tenantName,
  isSubmitting,
  onSubmit,
  platformAdminPinned = false,
  emailConfigured,
  signInSetting = null,
  knownSignInMethod,
  platformAdminRuleLoading = false,
  adminSignInMethods,
  canResetSignIn = false,
  error = null,
  roleChange = null,
  onCancelRoleChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  scope: AccessScope;
  grantorRole: RoleName;
  selfEmail: string;
  instanceName: string;
  tenantName: string;
  isSubmitting: boolean;
  onSubmit: (input: GrantInput) => void;
  /**
   * fix/grant-flow: the server pins a PlatformAdmin grant to its one workforce issuer (the issuer
   * view's `platformAdminPin`), so the dialog states the provider: a read-only note with one method offered, otherwise Microsoft selected and Email disabled (#1675).
   */
  platformAdminPinned?: boolean;
  /**
   * Whether the deployment can send the invitation email (the notifications view's
   * `emailConfigured`); undefined while unknown, which shows no line.
   */
  emailConfigured?: boolean;
  /**
   * #1660 review: how a person already on the list signs in (bound or pinned): the method, null
   * for a provider the dialog does not offer, undefined when the address is not on the list or
   * not yet pinned. A known method is shown read only and sent as is, so a grant never asks for a
   * change the server refuses with 409.
   */
  knownSignInMethod?: (email: string) => SignInMethod | null | undefined;
  /**
   * #1660 review: the issuer view that says whether a PlatformAdmin is workforce only is still
   * loading. A PlatformAdmin grant offers no Email choice until it has.
   */
  platformAdminRuleLoading?: boolean;
  /**
   * #1712: while the MFA mode covers admins (`GET /me` `mfa.adminSignInMethods`), the sign-in
   * methods an admin role can be granted with. An admin role then shows every other method
   * disabled with the reason, and a listed person on one cannot be granted it here. Absent, every
   * method is offered as before.
   */
  adminSignInMethods?: readonly SignInMethod[];
  /**
   * #1712: the reader can reset a person's sign-in (`platform.tenants.manage`, the Platform tab).
   * Otherwise a listed person on a method that cannot hold an admin role reads who can.
   */
  canResetSignIn?: boolean;
  /**
   * #1639 C0: the deployment's sign-in settings (`GET /api/auth/config`); the "Sign in with"
   * choice shows only when they offer both Microsoft and Email. Null or absent offers none.
   */
  signInSetting?: AuthClientSetting | null;
  /**
   * Session 10 (ux-s10 §3): the last refused grant, shown inside the dialog, which stays open
   * with what was typed. The parent closes it when the grant lands.
   */
  error?: InlineFailure | null;
  /** #1275: the person already holds another role here; the dialog asks to change it. */
  roleChange?: GrantRoleChange | null;
  /** #1275: Back from the confirmation to the form, as typed. */
  onCancelRoleChange?: () => void;
}) {
  // The tenant surface grants at tenant or instance scope only, so it never offers PlatformAdmin.
  const roles = useMemo(
    () =>
      grantableRoles(grantorRole).filter((role) => scope !== "tenant" || role !== "PlatformAdmin"),
    [grantorRole, scope],
  );
  const [email, setEmail] = useState("");
  const defaultRole = roles[roles.length - 1] ?? "Viewer";
  const [roleName, setRoleName] = useState<RoleName>(defaultRole);
  const [reason, setReason] = useState("");
  const [submitted, setSubmitted] = useState(false);
  // #1639 C0: the admin's own pick, for the address it was made for (#1660 review): a changed
  // address follows its own default again.
  const [methodChoice, setMethodChoice] = useState<{ email: string; method: SignInMethod } | null>(
    null,
  );
  // #2023: the two options show only after Change.
  const [changingMethod, setChangingMethod] = useState(false);

  // A dialog opens empty: a half-typed address must not reappear the next time
  // somebody grants access to a different person.
  //
  // Adjusted during RENDER off the previous `open`, which is React's documented
  // way to reset state when a prop changes. An effect would do the same work one
  // paint later, so the reader would see the last person's address flash by, and
  // `react-hooks/set-state-in-effect` refuses it for exactly that reason.
  useResetOnOpen(open, () => {
    setEmail("");
    setReason("");
    setRoleName(defaultRole);
    setSubmitted(false);
    setMethodChoice(null);
    setChangingMethod(false);
  });

  // #1675: a role that restricts the sign-in method (Platform admin under the workforce rule, an
  // admin role under the MFA floor) drops an earlier explicit pick, whatever order the admin chose
  // in, so the rule's method is sent and switching back starts from the address default again,
  // shown as the one line (#2023).
  function selectRole(next: RoleName) {
    const offered = offeredSignInMethods(signInSetting ?? undefined);
    const restricts =
      (next === "PlatformAdmin" && (platformAdminPinned || platformAdminRuleLoading)) ||
      (isMfaAdminRole(next) &&
        adminSignInMethods !== undefined &&
        offered.some((method) => !adminSignInMethods.includes(method)));
    if (next !== roleName && restricts) {
      setMethodChoice(null);
      setChangingMethod(false);
    }
    setRoleName(next);
  }

  const scopeType = scopeForRole(roleName);
  const scopeName = scopeNameOf(scopeType, tenantName, instanceName);

  // SEC-18: decided by the role (and the platform surface), never by the tab alone; it becomes
  // break-glass when the grantor is also the grantee at platform scope.
  const reasonRequired = grantNeedsReason(roleName, scopeType, scope === "platform");
  const providerFixed = roleName === "PlatformAdmin" && platformAdminPinned;
  // #1639 C0: a method is sent only when both exist; #2023 decides what the dialog shows of it.
  const offeredMethods = offeredSignInMethods(signInSetting ?? undefined);
  const offersMethod = offeredMethods.length > 1;
  const address = email.trim().toLowerCase();
  const known = offersMethod && address ? knownSignInMethod?.(address) : undefined;
  // A PlatformAdmin waits for the workforce rule to be known: Microsoft, and no choice yet.
  const methodLocked = roleName === "PlatformAdmin" && platformAdminRuleLoading;
  const chosen = methodChoice?.email === address ? methodChoice.method : undefined;
  // #1712: admins sign in with single sign-on while the MFA mode covers them.
  const ssoMethods = isMfaAdminRole(roleName) ? adminSignInMethods : undefined;
  const ssoRefuses = (method: SignInMethod) =>
    ssoMethods !== undefined && !ssoMethods.includes(method);
  const methodRefused = (method: SignInMethod) =>
    (providerFixed && method === "email") || ssoRefuses(method);
  // #1712: the sentence names only the offered methods that can hold an admin role; with none
  // left it says so, never a choice that is not there.
  const ssoMessage = adminNeedsSsoMessage(offeredMethods.filter((method) => !ssoRefuses(method)));
  // #2023: the methods the role allows; one left means nothing to show or choose.
  const allowedMethods = offeredMethods.filter(
    (method) => !methodRefused(method) && !(methodLocked && method === "email"),
  );
  const choice = signInMethodChoice(signInSetting ?? undefined, email, {
    allowed: allowedMethods,
    chosen,
  });
  let signInMethod: SignInMethod | undefined;
  // #1675: a listed person who is not on Microsoft cannot be a Platform admin here: say why, send no method.
  const workforceBlocked =
    providerFixed && offersMethod && known !== undefined && known !== "microsoft";
  // #1712: nor can a listed person on a method that cannot prove a second factor be an admin.
  const ssoBlocked = offersMethod && Boolean(known) && ssoRefuses(known as SignInMethod);
  // #1712 review: nor can anyone be made an admin here when no offered method can hold the role
  // (no provider declares MFA yet, or the one method this deployment offers cannot): Grant is off
  // and the dialog says why before submit, rather than a disabled choice staying selected.
  const noSsoMethod =
    ssoMethods !== undefined &&
    offeredMethods.length > 0 &&
    !workforceBlocked &&
    !ssoBlocked &&
    offeredMethods.every((method) => methodRefused(method));
  // #2023 review: while a Platform admin's rule loads, a grant that has no method left is off too,
  // never sent with no method; the dialog says nothing until the rule is known.
  const loadingBlocked =
    methodLocked && offersMethod && known === undefined && allowedMethods.length === 0;
  const ruleBlocked = workforceBlocked || ssoBlocked || noSsoMethod || loadingBlocked;
  if (!offersMethod || known === null || ruleBlocked) signInMethod = undefined;
  else if (known) signInMethod = known;
  else signInMethod = choice.method;
  // #2023: with no options on screen, the warning names the role's rule instead of the options.
  let personalEmailWarning = PERSONAL_EMAIL_WARNING;
  if (offersMethod && choice.shown === "none" && signInMethod === "microsoft") {
    personalEmailWarning =
      providerFixed || methodLocked
        ? PLATFORM_ADMIN_PERSONAL_EMAIL_WARNING
        : ADMIN_SSO_PERSONAL_EMAIL_WARNING;
  }
  // #2023: Change hands focus to the options it opens, so a keyboard user lands on them.
  // Only after a Change click, never when the options remount while the address is typed.
  const focusPending = useRef(false);
  const focusChecked = useCallback((node: HTMLDivElement | null) => {
    if (!node || !focusPending.current) return;
    focusPending.current = false;
    node.querySelector<HTMLElement>('[role="radio"][aria-checked="true"]')?.focus();
  }, []);
  const isBreakGlass =
    scopeType === "platform" && email.trim().toLowerCase() === selfEmail.toLowerCase();

  const emailError = emailErrorOf(email);
  const reasonError = reasonErrorOf(reasonRequired, reason);
  const canSubmit = !emailError && !reasonError && !ruleBlocked;
  const copy = dialogCopy(isBreakGlass, roleName, scopeName);
  // A field's message shows once Grant has been pressed.
  const shownEmailError = submitted ? emailError : undefined;
  const shownReasonError = submitted ? reasonError : undefined;

  function submit() {
    setSubmitted(true);
    if (!canSubmit) return;
    onSubmit({
      email: email.trim(),
      roleName,
      reason: reason.trim() || undefined,
      ...(signInMethod ? { signInMethod } : {}),
    });
  }

  if (roleChange) {
    const changeScope = scopeNameOf(
      scopeForRole(roleChange.input.roleName),
      tenantName,
      instanceName,
    );
    const current = rolesSentence(roleChange.currentRoles);
    // #1275 review: changing your own role says so, and a step down says what you lose.
    const isSelf = roleChange.input.email.trim().toLowerCase() === selfEmail.toLowerCase();
    const highestCurrent = roleChange.currentRoles.reduce<RoleName | null>(
      (top, role) => (top === null || ROLE_RANK[role] > ROLE_RANK[top] ? role : top),
      null,
    );
    const selfDemotion =
      isSelf &&
      highestCurrent !== null &&
      ROLE_RANK[roleChange.input.roleName] < ROLE_RANK[highestCurrent];
    return (
      <SkyDialog
        open={open}
        onOpenChange={onOpenChange}
        size="medium"
        testId="grant-dialog"
        onBeforeClose={(reason) => reason !== "primaryAction"}
        title={roleChangeQuestion(roleChange, changeScope)}
        description={`${roleChange.input.email} holds one role on ${changeScope}.`}
        primaryAction={{
          label: isSelf ? "Change my role" : "Change role",
          onClick: () => onSubmit({ ...roleChange.input, replaceExisting: true }),
          loading: isSubmitting,
        }}
        secondaryAction={{ label: "Back", onClick: () => onCancelRoleChange?.() }}
      >
        <div className="space-y-4" data-testid="grant-role-change">
          <SkyText as="p" block size={300}>
            {`${ROLE_LABELS[roleChange.input.roleName]} replaces ${current}. The audit trail records the change with ${current} as the role before it.`}
          </SkyText>
          {selfDemotion && highestCurrent ? (
            <SkyMessageBar
              intent="warning"
              layout="multiline"
              testId="grant-self-demotion"
              title="This is your own role"
              message={`You will no longer be ${ROLE_LABELS[highestCurrent]} on ${changeScope}. You lose what that role lets you do here, and someone else has to give it back.`}
            />
          ) : null}
          <InlineErrorBar
            error={error}
            title={error?.retryable ? undefined : "The role was not changed"}
            testId="grant-error"
          />
        </div>
      </SkyDialog>
    );
  }

  return (
    <SkyDialog
      open={open}
      onOpenChange={onOpenChange}
      size="medium"
      testId="grant-dialog"
      // Never closed by the primary action itself: an incomplete form keeps its messages and a
      // refused grant keeps its reason on screen; the parent closes it on success.
      onBeforeClose={(reason) => reason !== "primaryAction"}
      title={copy.title}
      description={copy.description}
      primaryAction={{
        label: copy.action,
        onClick: submit,
        loading: isSubmitting,
        disabled: ruleBlocked,
      }}
      secondaryAction={{ label: "Cancel", onClick: () => onOpenChange(false) }}
    >
      <div className="space-y-4">
        <SkyInput
          label="Email address"
          type="email"
          required
          value={email}
          testId="grant-email"
          autoComplete="off"
          placeholder="name@example.com"
          validationState={shownEmailError ? "error" : "none"}
          validationMessage={shownEmailError}
          onChange={(event) => setEmail(event.target.value)}
        />

        {isPersonalEmail(email) && signInMethod !== "email" ? (
          <SkyMessageBar
            intent="warning"
            layout="multiline"
            testId="grant-personal-email-warning"
            message={personalEmailWarning}
          />
        ) : null}

        <SkyDropdown
          label="Role"
          required
          testId="grant-role"
          selectedOptions={[roleName]}
          listboxMaxWidth="420px"
          options={roles.map((role) => ({
            value: role,
            text: `${ROLE_LABELS[role]} — ${ROLE_DESCRIPTIONS[role]}`,
            selectedText: ROLE_LABELS[role],
          }))}
          onOptionSelect={(values) => selectRole((values[0] as RoleName) ?? roleName)}
        />

        {/* Scope is a consequence of the role, so it is stated, not chosen. */}
        <SkyInput
          label="Scope"
          readOnly
          value={scopeName}
          testId="grant-scope"
          hint={scopeHint(roles)}
        />

        {workforceBlocked || ssoBlocked ? (
          <SkyInput
            label="Sign in with"
            readOnly
            value={known ? SIGN_IN_METHOD_LABELS[known] : "Another provider"}
            testId="grant-provider-fixed-blocked"
            hint={
              workforceBlocked
                ? PLATFORM_ADMIN_KNOWN_METHOD_NOTE
                : adminNeedsSsoResetMessage(canResetSignIn)
            }
          />
        ) : null}

        {noSsoMethod ? (
          <SkyMessageBar
            intent="warning"
            layout="multiline"
            testId="grant-admin-sso-unavailable"
            message={ssoMessage}
          />
        ) : null}

        {/* #2023: a listed person's method, one line with no Change, where the role allows a
            choice at all. */}
        {known && !ruleBlocked && choice.shown === "line" ? (
          <div data-testid="grant-sign-in-method-known">
            <SkyText as="p" block size={300}>
              {SIGN_IN_METHOD_LINES[known]}
            </SkyText>
            <SkyText as="p" block size={200} color="muted">
              This person already signs in this way. Reset their sign-in to change it.
            </SkyText>
          </div>
        ) : null}

        {known === undefined && signInMethod && choice.shown === "line" && !changingMethod ? (
          <SkyText as="p" block size={300} testId="grant-sign-in-method-line">
            {SIGN_IN_METHOD_LINES[signInMethod]}{" "}
            <SkyLink
              testId="grant-sign-in-method-change"
              aria-label="Change how this person signs in"
              onClick={() => {
                focusPending.current = true;
                setChangingMethod(true);
              }}
            >
              Change
            </SkyLink>
          </SkyText>
        ) : null}

        {known === undefined && signInMethod && choice.shown === "line" && changingMethod ? (
          <div ref={focusChecked}>
            <SkyRadioGroup
              label="Sign in with"
              name="grant-sign-in-method"
              testId="grant-sign-in-method"
              required
              value={signInMethod}
              onChange={(value) =>
                setMethodChoice({ email: address, method: value as SignInMethod })
              }
              options={allowedMethods.map((method) => ({
                value: method,
                label: SIGN_IN_METHOD_LABELS[method],
                description: SIGN_IN_METHOD_HELP[method],
              }))}
            />
          </div>
        ) : null}

        {reasonRequired ? (
          <SkyInput
            label="Reason"
            required
            value={reason}
            testId="grant-reason"
            hint={`Recorded on the audit event. ${MIN_REASON_LENGTH} characters minimum.`}
            validationState={shownReasonError ? "error" : "none"}
            validationMessage={shownReasonError}
            onChange={(event) => setReason(event.target.value)}
          />
        ) : null}

        {isBreakGlass ? (
          <SkyMessageBar
            intent="warning"
            layout="multiline"
            testId="break-glass-warning"
            title="This is recorded as break-glass access"
            message="Granting yourself a role at platform scope writes access.grant with metadata.break_glass = true. The access review report lists break-glass grants first."
          />
        ) : null}

        {/* One paragraph (fixes wave 3): as two inline `SkyText`s the note and this sentence ran
            together as "address.The person". */}
        <SkyText as="p" block size={200} color="muted">
          {emailConfigured === undefined ? null : (
            <InviteNote emailConfigured={emailConfigured} emailMethod={signInMethod === "email"} />
          )}
          A person who has not signed in yet appears as Pending until their first sign-in, which
          records their directory id and activates the account.
        </SkyText>

        <InlineErrorBar
          error={error}
          title={error?.retryable ? undefined : "The grant was not saved"}
          testId="grant-error"
        />
      </div>
    </SkyDialog>
  );
}
